import React, { useEffect, useRef, useState } from 'react';
import { markGamePlayed } from '../utils/storage';

const W = 360;
const H = 540;
const PLAYER_W = 40;
const PLAYER_H = 20;
const BULLET_SPEED = 8;
const ENEMY_ROWS = 3;
const ENEMY_COLS = 7;
const ENEMY_W = 30;
const ENEMY_H = 20;

function initEnemies() {
  const enemies = [];
  for (let r = 0; r < ENEMY_ROWS; r++) {
    for (let c = 0; c < ENEMY_COLS; c++) {
      enemies.push({
        id: `${r}-${c}`,
        x: 20 + c * 46,
        y: 40 + r * 40,
        alive: true,
      });
    }
  }
  return enemies;
}

export default function ShootingGame({ onDone, childId }) {
  const canvasRef = useRef(null);
  const childIdRef = useRef(childId); // useEffect内で使うためrefで保持
  const stateRef = useRef({
    player: { x: W / 2 - PLAYER_W / 2, y: H - 50 },
    bullets: [],
    enemies: initEnemies(),
    score: 0,
    over: false,
    won: false,
    keys: {},
    lastShot: 0,
  });
  const [display, setDisplay] = useState({ score: 0, over: false, won: false });
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const s = stateRef.current;

    const onKey = (e) => {
      s.keys[e.code] = e.type === 'keydown';
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);

    let enemyDir = 1;
    let enemyMoveTimer = 0;

    const loop = (ts) => {
      if (s.over || s.won) return;

      // プレイヤー移動
      if (s.keys['ArrowLeft'] || s.keys['KeyA']) s.player.x = Math.max(0, s.player.x - 5);
      if (s.keys['ArrowRight'] || s.keys['KeyD']) s.player.x = Math.min(W - PLAYER_W, s.player.x + 5);

      // 射撃（スペース）
      if ((s.keys['Space'] || s.keys['ArrowUp']) && ts - s.lastShot > 300) {
        s.bullets.push({ x: s.player.x + PLAYER_W / 2, y: s.player.y });
        s.lastShot = ts;
      }

      // 弾移動
      s.bullets = s.bullets.filter((b) => b.y > 0);
      s.bullets.forEach((b) => (b.y -= BULLET_SPEED));

      // 敵移動
      enemyMoveTimer += 16;
      if (enemyMoveTimer > 600) {
        enemyMoveTimer = 0;
        const aliveEnemies = s.enemies.filter((e) => e.alive);
        const minX = Math.min(...aliveEnemies.map((e) => e.x));
        const maxX = Math.max(...aliveEnemies.map((e) => e.x));
        if (maxX + ENEMY_W >= W) enemyDir = -1;
        if (minX <= 0) enemyDir = 1;
        aliveEnemies.forEach((e) => {
          e.x += enemyDir * 15;
          e.y += 5;
        });
        if (aliveEnemies.some((e) => e.y + ENEMY_H >= H - 60)) {
          s.over = true;
          markGamePlayed(childIdRef.current);
          setDisplay({ score: s.score, over: true, won: false });
          return;
        }
      }

      // 衝突判定
      s.bullets.forEach((b) => {
        s.enemies.forEach((e) => {
          if (!e.alive) return;
          if (b.x >= e.x && b.x <= e.x + ENEMY_W && b.y >= e.y && b.y <= e.y + ENEMY_H) {
            e.alive = false;
            b.y = -100;
            s.score += 10;
          }
        });
      });

      // クリア判定
      if (s.enemies.every((e) => !e.alive)) {
        s.won = true;
        markGamePlayed();
        setDisplay({ score: s.score, over: false, won: true });
        return;
      }

      // 描画
      ctx.fillStyle = '#0a0a1a';
      ctx.fillRect(0, 0, W, H);

      // プレイヤー
      ctx.fillStyle = '#00ff88';
      ctx.fillRect(s.player.x, s.player.y, PLAYER_W, PLAYER_H);

      // 弾
      ctx.fillStyle = '#ffff00';
      s.bullets.forEach((b) => ctx.fillRect(b.x - 2, b.y, 4, 10));

      // 敵
      s.enemies.filter((e) => e.alive).forEach((e) => {
        ctx.font = '20px serif';
        ctx.fillText('👾', e.x, e.y + ENEMY_H);
      });

      // スコア
      ctx.fillStyle = '#fff';
      ctx.font = '14px monospace';
      ctx.fillText(`スコア: ${s.score}`, 10, 20);

      setDisplay((d) => ({ ...d, score: s.score }));
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
    };
  }, []);

  // タッチ操作
  const touchLeft = () => { stateRef.current.keys['ArrowLeft'] = true; };
  const touchRight = () => { stateRef.current.keys['ArrowRight'] = true; };
  const touchShoot = () => { stateRef.current.keys['Space'] = true; };
  const touchRelease = () => {
    stateRef.current.keys['ArrowLeft'] = false;
    stateRef.current.keys['ArrowRight'] = false;
    stateRef.current.keys['Space'] = false;
  };

  return (
    <div className="screen shooting-screen">
      <h2 className="shooting-title">🚀 シューティング！</h2>

      {(display.over || display.won) ? (
        <div className="game-over-box">
          <div className="game-over-icon">{display.won ? '🏆' : '💥'}</div>
          <div className="game-over-text">{display.won ? 'クリア！' : 'ゲームオーバー'}</div>
          <div className="game-over-score">スコア：{display.score}</div>
          <button className="btn-home" onClick={onDone}>🏠 ホームへ</button>
        </div>
      ) : (
        <>
          <canvas ref={canvasRef} width={W} height={H} className="game-canvas" />
          {/* タッチコントロール */}
          <div className="touch-controls">
            <button
              className="touch-btn"
              onTouchStart={touchLeft} onTouchEnd={touchRelease}
              onMouseDown={touchLeft} onMouseUp={touchRelease}
            >◀</button>
            <button
              className="touch-btn touch-shoot"
              onTouchStart={touchShoot} onTouchEnd={touchRelease}
              onMouseDown={touchShoot} onMouseUp={touchRelease}
            >🔫</button>
            <button
              className="touch-btn"
              onTouchStart={touchRight} onTouchEnd={touchRelease}
              onMouseDown={touchRight} onMouseUp={touchRelease}
            >▶</button>
          </div>
        </>
      )}
    </div>
  );
}
